import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';
import UserStats from './UserStats';
import UserList from './UserList';
import UserDrawer from './UserDrawer';
import { AdminResetMfaDialog } from '../MFA';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Drawer State
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // If null, creating new user
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Delete State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Admin MFA reset (recovery) State
  const [resetMfaTarget, setResetMfaTarget] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/users');
      setUsers(response.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // --- Actions ---

  const handleCreateClick = () => {
    setSelectedUser(null);
    setShowDrawer(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setShowDrawer(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleSaveUser = async (formData, isEditMode, passwordChanged) => {
    setDrawerLoading(true);
    try {
      if (isEditMode) {
        // Update User
        // Only send changed fields and required fields
        const updateData = {};
        if (formData.email !== selectedUser.email) updateData.email = formData.email;
        if (formData.name !== selectedUser.name) updateData.name = formData.name;
        if (formData.phone !== selectedUser.phone) updateData.phone = formData.phone;
        if (formData.address !== selectedUser.address) updateData.address = formData.address;
        if (formData.company !== selectedUser.company) updateData.company = formData.company;
        if (formData.role !== selectedUser.role) updateData.role = formData.role;

        // Include service_states for vendors
        if (formData.role === 'vendor') {
          // Basic check if array changed (simple length or content check might be needed)
          // For simplicity, just sending it if it exists for vendors
          updateData.service_states = formData.service_states;
        }

        if (passwordChanged && formData.password) {
          updateData.password = formData.password;
        }

        await apiCall(`/api/users/${selectedUser.id}`, {
          method: 'PUT',
          body: JSON.stringify(updateData),
        });
        showToast.success('User updated successfully');
      } else {
        const dataToSend = {
          ...formData,
          password: formData.password
        };
        await apiCall('/api/users', {
          method: 'POST',
          body: JSON.stringify(dataToSend),
        });
        showToast.success('User created successfully');
      }


      setShowDrawer(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      showToast.error(error.message || 'Failed to save user');
    } finally {
      setDrawerLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      await apiCall(`/api/users/${userToDelete.id}`, {
        method: 'DELETE',
      });
      showToast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast.error(error.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Prevent browser window scrolling while on users page; restore on unmount
  // This matches the Orders page behavior
  useEffect(() => {
    const previousOverflowY = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = previousOverflowY;
    };
  }, []);

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header - flex-shrink-0 so it never collapses */}
      <div className="flex-shrink-0 flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage system access and user roles</p>
        </div>
        <button
          onClick={handleCreateClick}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New User
        </button>
      </div>

      {/* Stats Dashboard - flex-shrink-0 so it never collapses */}
      <div className="flex-shrink-0 mb-2">
        <UserStats users={users} />
      </div>

      {/* Main List - flex-1 min-h-0 so it fills remaining space and scrolls internally */}
      <div className="flex-1 min-h-0">
        <UserList
          users={users}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onResetMfa={(user) => setResetMfaTarget(user)}
        />
      </div>

      {/* Create/Edit Drawer */}
      <UserDrawer
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        user={selectedUser}
        onSave={handleSaveUser}
        loading={drawerLoading}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete User?</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin MFA Reset (recovery) Dialog */}
      {resetMfaTarget && (
        <AdminResetMfaDialog
          targetUser={resetMfaTarget}
          onClose={() => setResetMfaTarget(null)}
          onDone={() => {
            setResetMfaTarget(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;